import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard  {
  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {

      // Check if this is a portal transfer with tokens in the URL
      // If so, process the tokens and WAIT for user data to load before allowing navigation
      if (this.isPortalTransfer()) {
        return this.processPortalTransfer();
      }

      if (!this.authService.isAuthenticated()) {
        this.router.navigate(['login']);
        return false;
      }
      return true;
  }

  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  private isPortalTransfer(): boolean {
    // Check query parameters (before hash) since app uses hash-based routing
    const search = window.location.search;
    return search && search.includes('portal_transfer=true');
  }

  private processPortalTransfer(): Promise<boolean> {
    // Parse query parameters (window.location.search)
    const params = this.parseQueryParams(window.location.search);

    if (params.access_token && params.id_token && params.expires_in) {
      console.log('Auth guard: Processing portal transfer tokens');

      // Calculate expires_at from expires_in (seconds)
      const expiresAt = JSON.stringify(
        (parseInt(params.expires_in) * 1000) + new Date().getTime()
      );

      // Set tokens in localStorage immediately
      localStorage.setItem('access_token', params.access_token);
      localStorage.setItem('id_token', params.id_token);
      localStorage.setItem('expires_at', expiresAt);

      // Clear query params from URL but preserve hash routing
      const cleanUrl = window.location.origin + window.location.pathname + window.location.hash;
      window.history.replaceState(null, '', cleanUrl);

      // Wait for auth service to complete session setup (including user data)
      // This returns a Promise that resolves when user profile is fully loaded
      return this.authService.handlePortalTransfer();
    }

    // If params are missing, reject the transfer
    console.error('Auth guard: Portal transfer missing required params');
    this.router.navigate(['login']);
    return Promise.resolve(false);
  }

  private parseQueryParams(search: string): any {
    const params: any = {};
    // Remove leading '?' and split by '&'
    if (search && search.length > 1) {
      search.substring(1).split('&').forEach(param => {
        const parts = param.split('=');
        if (parts.length === 2) {
          params[parts[0]] = decodeURIComponent(parts[1]);
        }
      });
    }
    return params;
  }
}

